import { useEffect, useMemo, useRef, useState } from 'react';
import { Save, Trash2, ClipboardList, Ruler, IndianRupee, FileText, Package, AlertTriangle } from '@/components/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup, FormSection } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { SmartSelect } from '@/components/form/SmartSelect';
import { useToast } from '@/components/ui/useToast';
import { createItem, updateItem, uploadItemImage, deleteItemImage, type Item } from '@/api/items';
import { apiError } from '@/lib/apiError';
import { useAuth } from '@/auth/useAuth';

interface Props {
  open: boolean;
  editing: Item | null;
  onClose: () => void;
}

type FormState = {
  code: string;
  name: string;
  barcode: string;
  category: string;
  unit: string;
  hsn: string;
  gstPct: string;
  width: string;
  length: string;
  sizeUnit: 'cm' | 'inch' | 'ft' | 'm' | '';
  gsm: string;
  color: string;
  style: string;
  collection: string;
  brand: string;
  purchaseRate: string;
  additionalCost: string;
  marginPct: string;
  mrp: string;
  reorderLevel: string;
  netWeightKg: string;
  grossWeightKg: string;
  openingQty: string;
  description: string;
  status: 'Active' | 'Inactive';
};

function emptyForm(): FormState {
  return {
    code: '', name: '', barcode: '', category: '', unit: '', hsn: '',
    gstPct: '12', width: '', length: '', sizeUnit: '', gsm: '',
    color: '', style: '', collection: '', brand: '',
    purchaseRate: '', additionalCost: '', marginPct: '', mrp: '', reorderLevel: '', netWeightKg: '', grossWeightKg: '',
    openingQty: '',
    description: '', status: 'Active',
  };
}

/** Metres in one size unit — width × length in the item's unit → area in m². */
const M_PER_UNIT: Record<string, number> = { cm: 0.01, inch: 0.0254, ft: 0.3048, m: 1 };

/** Stored units come in mixed spellings ("CM", "FT", "mtr") — map them to the form's four. */
function normUnit(u: string | null | undefined): FormState['sizeUnit'] {
  const s = (u ?? '').trim().toLowerCase();
  if (/^(cm|cms|centimet(er|re)s?)$/.test(s)) return 'cm';
  if (/^(in|inch|inches|")$/.test(s)) return 'inch';
  if (/^(ft|feet|foot)$/.test(s)) return 'ft';
  if (/^(m|mtr|mtrs|met(er|re)s?)$/.test(s)) return 'm';
  return '';
}

/** Area of one piece in m², or null until width, length and unit are all set. */
function areaM2(f: Pick<FormState, 'width' | 'length' | 'sizeUnit'>): number | null {
  const w = Number(f.width), l = Number(f.length), k = M_PER_UNIT[f.sizeUnit];
  return k && w > 0 && l > 0 ? w * k * (l * k) : null;
}

/** Required on every item (the server checks the same): MRP, size, GSM, net weight. */
function missingSpecs(f: FormState): string[] {
  const pos = (v: string) => Number(v) > 0;
  return [
    !pos(f.mrp) && 'MRP', !pos(f.width) && 'Width', !pos(f.length) && 'Length', !f.sizeUnit && 'Size Unit',
    !pos(f.gsm) && 'GSM', !pos(f.netWeightKg) && 'Net Weight',
  ].filter((x): x is string => !!x);
}

/** Which of GSM / net weight the form filled in itself (the other is the user's). */
type Derived = 'gsm' | 'net' | null;

/**
 * GSM ↔ net weight through the size: weight (kg) = GSM × area (m²) ÷ 1000.
 * The field just typed drives the other — but a value the user entered is never
 * overwritten; only an empty or form-calculated one is (re)filled. A size or unit
 * change recalculates the calculated one from the user's.
 */
function withWeight(f: FormState, derived: Derived, typed: 'gsm' | 'net' | 'size'): { form: FormState; derived: Derived } {
  if (typed === derived) derived = null; // typing over a calculated value makes it the user's
  const from = typed === 'size' ? (derived === 'net' ? 'gsm' : derived === 'gsm' ? 'net' : null) : typed;
  const area = areaM2(f);
  if (!from || !area) return { form: f, derived };
  const to = from === 'gsm' ? 'net' : 'gsm';
  const toKey = to === 'net' ? 'netWeightKg' : 'gsm';
  const src = Number(from === 'gsm' ? f.gsm : f.netWeightKg);
  if (!(src > 0)) return derived === to ? { form: { ...f, [toKey]: '' }, derived: null } : { form: f, derived };
  if (f[toKey] !== '' && derived !== to) return { form: f, derived }; // the user's own value stays
  const value = to === 'net' ? String(Math.round((src * area) / 10) / 100) : String(Math.round((src * 1000) / area));
  return { form: { ...f, [toKey]: value }, derived: to };
}

function fromItem(item: Item): FormState {
  return {
    code: item.code,
    name: item.name,
    barcode: item.barcode ?? '',
    category: item.category,
    unit: item.unit,
    hsn: item.hsn ?? '',
    gstPct: item.gstPct,
    width: item.width ?? '',
    length: item.length ?? '',
    sizeUnit: normUnit(item.sizeUnit),
    gsm: item.gsm != null ? String(item.gsm) : '',
    color: item.color ?? '',
    style: item.style ?? '',
    collection: item.collection ?? '',
    brand: item.brand ?? '',
    purchaseRate: item.purchaseRate,
    additionalCost: item.additionalCost ?? '',
    marginPct: item.marginPct ?? '',
    mrp: item.mrp,
    reorderLevel: item.reorderLevel != null ? String(item.reorderLevel) : '',
    netWeightKg: item.netWeightKg ?? '',
    grossWeightKg: item.grossWeightKg ?? '',
    openingQty: item.warehouseStock ? String(item.warehouseStock.openingQty) : '',
    description: item.description ?? '',
    status: item.status,
  };
}

function toPayload(f: FormState, includeOpeningQty: boolean): Record<string, unknown> {
  const out: Record<string, unknown> = {
    code: f.code.trim().replace(/\s+/g, ' ').toUpperCase(),
    name: f.name.trim(),
    category: f.category,
    unit: f.unit,
    status: f.status,
  };
  if (f.barcode.trim()) out.barcode = f.barcode.trim();
  if (f.hsn.trim()) out.hsn = f.hsn.trim();
  const numeric: [keyof FormState, string][] = [
    ['gstPct', 'gstPct'], ['width', 'width'], ['length', 'length'], ['gsm', 'gsm'],
    ['purchaseRate', 'purchaseRate'], ['additionalCost', 'additionalCost'], ['marginPct', 'marginPct'], ['mrp', 'mrp'],
    ['reorderLevel', 'reorderLevel'], ['netWeightKg', 'netWeightKg'],
    ['grossWeightKg', 'grossWeightKg'],
  ];
  for (const [k, target] of numeric) {
    const raw = f[k];
    if (raw !== '' && raw != null) out[target] = Number(raw);
  }
  if (f.sizeUnit) out.sizeUnit = f.sizeUnit;
  if (f.color) out.color = f.color;
  if (f.style) out.style = f.style;
  if (f.collection) out.collection = f.collection;
  if (f.brand) out.brand = f.brand;
  if (f.description.trim()) out.description = f.description.trim();
  // openingQty only when the caller holds `items-opening-stock` — the field isn't
  // rendered otherwise, but fromItem() still pre-fills it from warehouseStock, and
  // the backend rejects the whole save if a non-permitted user sends it.
  if (includeOpeningQty && f.openingQty !== '' && f.openingQty != null) out.openingQty = Number(f.openingQty);
  return out;
}

export function ItemFormModal({ open, editing, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [deleteImage, setDeleteImage] = useState(false);
  const [derived, setDerived] = useState<Derived>(null);
  // After a save attempt, the required specs still empty show as errors.
  const [tried, setTried] = useState(false);
  const missing = missingSpecs(form);
  const need = (label: string) => (tried && missing.includes(label) ? 'Required' : null);
  /** Edit size / GSM / net weight, keeping the other of GSM ↔ net weight in step. */
  const setSized = (patch: Partial<FormState>, typed: 'gsm' | 'net' | 'size') => {
    const r = withWeight({ ...form, ...patch }, derived, typed);
    setForm(r.form);
    setDerived(r.derived);
  };
  const area = areaM2(form);
  // Both entered by hand but they disagree with the size: show what the size gives.
  const weightCheck = (() => {
    if (!area || derived || !(Number(form.gsm) > 0) || !(Number(form.netWeightKg) > 0)) return null;
    const calc = (Number(form.gsm) * area) / 1000;
    return Math.abs(calc - Number(form.netWeightKg)) / calc > 0.05 ? `GSM × size = ${calc.toFixed(2)} kg` : null;
  })();
  const { hasPerm } = useAuth();
  const canSetOpeningStock = hasPerm('items-opening-stock');

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm(fromItem(editing));
      setImagePreview(editing.imageUrl ?? null);
    } else {
      setForm(emptyForm());
      setImagePreview(null);
    }
    setPendingImage(null);
    setDeleteImage(false);
    setDerived(null);
    setTried(false);
  }, [open, editing]);

  // Auto-MRP from cost + margin.
  useEffect(() => {
    const cost = Number(form.purchaseRate);
    const margin = Number(form.marginPct);
    if (form.purchaseRate && form.marginPct && !Number.isNaN(cost) && !Number.isNaN(margin)) {
      const mrp = Math.round(cost * (1 + margin / 100));
      setForm((s) => ({ ...s, mrp: String(mrp) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.purchaseRate, form.marginPct]);

  const editingId = editing?.id ?? null;
  const title = useMemo(() => (editingId ? `Edit Item — ${editing?.code}` : 'Add Item'), [editing, editingId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toPayload(form, canSetOpeningStock);
      // The code field is disabled while editing — resend the stored code verbatim
      // so toPayload's normalization (trim/collapse/uppercase) can't silently
      // rename legacy codes (e.g. double spaces) or trip backend validation.
      if (editingId && editing) payload.code = editing.code;
      const saved = editingId
        ? await updateItem(editingId, payload)
        : await createItem(payload);
      if (deleteImage && editingId) await deleteItemImage(editingId);
      else if (pendingImage) await uploadItemImage(saved.id, pendingImage);
      return saved;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
      toast.push(editingId ? 'Item updated' : 'Item created', 'success');
      onClose();
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Save failed'), 'error');
    },
  });

  function onSelectImage(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingImage(f);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(typeof reader.result === 'string' ? reader.result : null);
    reader.readAsDataURL(f);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => {
              if (missing.length) {
                setTried(true);
                toast.push(`Fill in ${missing.join(', ')} — required for every item.`, 'error');
                return;
              }
              saveMutation.mutate();
            }}
            disabled={saveMutation.isPending || !form.code || !form.name || !form.category || !form.unit}
          >
            {saveMutation.isPending ? 'Saving…' : (<span className="inline-flex items-center gap-1"><Save size={14} /> Save Item</span>)}
          </Button>
        </>
      }
    >
      {/* Header: image + basic */}
      <div className="mb-5 grid grid-cols-[100px_1fr] items-start gap-4 border-b border-[var(--border-color)] pb-4">
        <div className="flex flex-col items-center gap-2">
          <div
            onClick={() => fileInputRef.current?.click()}
            className="flex h-[90px] w-[90px] cursor-pointer items-center justify-center overflow-hidden rounded-card border-2 border-dashed border-[var(--border-color)] bg-[var(--input-bg)]"
          >
            {imagePreview ? (
              <img src={imagePreview} alt="" className="h-full w-full object-cover" />
            ) : (
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={onSelectImage} className="hidden" />
          <small className="text-center text-[10px] text-[var(--text-muted)]">Click to browse</small>
          {imagePreview && (
            <button
              type="button"
              onClick={() => { setImagePreview(null); setPendingImage(null); setDeleteImage(true); }}
              className="flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold text-red-500 hover:bg-red-500/10"
              title="Remove image"
            >
              <Trash2 size={12} /> Remove
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormGroup label="Item Code / SKU" required>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="e.g. CRP-001"
              disabled={!!editingId}
            />
          </FormGroup>
          <FormGroup label="Item Name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full product name" />
          </FormGroup>
          <FormGroup
            label={
              <span>
                Barcode <small className="font-medium text-accent">(immutable once saved)</small>
              </span>
            }
          >
            <Input
              value={form.barcode}
              onChange={(e) => setForm({ ...form, barcode: e.target.value })}
              placeholder={editing ? 'Optional' : 'Auto GRM000001… if blank'}
              disabled={!!editing?.barcode}
              style={{ fontFamily: 'monospace', letterSpacing: 1 }}
            />
          </FormGroup>
          <FormGroup label="Status">
            <Select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as 'Active' | 'Inactive' })}
            >
              <option>Active</option>
              <option>Inactive</option>
            </Select>
          </FormGroup>
        </div>
      </div>

      <FormSection title={<span className="inline-flex items-center gap-1.5"><ClipboardList size={14} /> Classification</span>}>
        <div className="grid grid-cols-2 gap-3">
          <FormGroup label="Category" required>
            <SmartSelect listKey="category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
          </FormGroup>
          <FormGroup label="Unit of Measure" required>
            <SmartSelect listKey="unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} />
          </FormGroup>
          <FormGroup label="HSN Code">
            <Input
              value={form.hsn}
              onChange={(e) => setForm({ ...form, hsn: e.target.value })}
              placeholder="e.g. 57011010"
              maxLength={8}
            />
          </FormGroup>
          <FormGroup label="GST Rate %">
            <Select value={form.gstPct} onChange={(e) => setForm({ ...form, gstPct: e.target.value })}>
              <option value="0">0% — Nil</option>
              <option value="5">5% — Essential</option>
              <option value="12">12% — Standard</option>
              <option value="18">18% — Standard</option>
              <option value="28">28% — Luxury</option>
            </Select>
          </FormGroup>
        </div>
      </FormSection>

      <FormSection title={<span className="inline-flex items-center gap-1.5"><Ruler size={14} /> Size & Design</span>}>
        <div className="grid grid-cols-4 gap-3">
          <FormGroup label="Width (W)" required error={need('Width')}>
            <Input type="number" value={form.width} onChange={(e) => setSized({ width: e.target.value }, 'size')} />
          </FormGroup>
          <FormGroup label="Length (L)" required error={need('Length')}>
            <Input type="number" value={form.length} onChange={(e) => setSized({ length: e.target.value }, 'size')} />
          </FormGroup>
          <FormGroup label="Size Unit" required error={need('Size Unit')} hint={area ? `${area.toFixed(2)} m² · ${(area * 10.7639).toFixed(2)} sq ft` : undefined}>
            <Select
              value={form.sizeUnit}
              onChange={(e) => setSized({ sizeUnit: e.target.value as FormState['sizeUnit'] }, 'size')}
            >
              <option value="">—</option>
              <option value="cm">cm</option>
              <option value="inch">inch</option>
              <option value="ft">ft</option>
              <option value="m">m</option>
            </Select>
          </FormGroup>
          <FormGroup label="GSM" required error={need('GSM')} hint={derived === 'gsm' ? 'calculated from net weight ÷ size' : area ? 'fills net weight' : undefined}>
            <Input type="number" value={form.gsm} onChange={(e) => setSized({ gsm: e.target.value }, 'gsm')} />
          </FormGroup>
          <FormGroup label="Color / Design">
            <SmartSelect listKey="color" value={form.color} onChange={(v) => setForm({ ...form, color: v })} />
          </FormGroup>
          <FormGroup label="Style / Pattern">
            <SmartSelect listKey="style" value={form.style} onChange={(v) => setForm({ ...form, style: v })} />
          </FormGroup>
          <FormGroup label="Collection">
            <SmartSelect listKey="collection" value={form.collection} onChange={(v) => setForm({ ...form, collection: v })} />
          </FormGroup>
          <FormGroup label="Brand">
            <SmartSelect listKey="brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />
          </FormGroup>
        </div>
      </FormSection>

      <FormSection title={<span className="inline-flex items-center gap-1.5"><IndianRupee size={14} /> Pricing</span>}>
        <div className="grid grid-cols-4 gap-3">
          <FormGroup label="Purchase Cost (₹)">
            <Input type="number" step="0.01" value={form.purchaseRate} onChange={(e) => setForm({ ...form, purchaseRate: e.target.value })} />
          </FormGroup>
          <FormGroup label="Additional Cost (₹)" hint="freight, duty, etc.">
            <Input type="number" step="0.01" value={form.additionalCost} onChange={(e) => setForm({ ...form, additionalCost: e.target.value })} />
          </FormGroup>
          <FormGroup label="Margin %" hint="e.g. 150 = 2.5×">
            <Input type="number" value={form.marginPct} onChange={(e) => setForm({ ...form, marginPct: e.target.value })} />
          </FormGroup>
          <FormGroup label="MRP (₹)" required error={need('MRP')} hint="Auto from cost+margin; can override">
            <Input
              type="number"
              step="0.01"
              value={form.mrp}
              onChange={(e) => setForm({ ...form, mrp: e.target.value })}
              className="font-bold text-accent"
            />
          </FormGroup>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <FormGroup label="Reorder Level (Min Stock)">
            <Input type="number" value={form.reorderLevel} onChange={(e) => setForm({ ...form, reorderLevel: e.target.value })} />
          </FormGroup>
          <FormGroup label="Net Weight / unit (kg)" required error={need('Net Weight')} hint={derived === 'net' ? 'calculated: GSM × size' : weightCheck ?? (area ? 'fills GSM' : undefined)}>
            <Input type="number" step="0.01" value={form.netWeightKg} onChange={(e) => setSized({ netWeightKg: e.target.value }, 'net')} />
          </FormGroup>
          <FormGroup label="Gross Weight / unit (kg)" hint="incl. packaging">
            <Input type="number" step="0.01" value={form.grossWeightKg} onChange={(e) => setForm({ ...form, grossWeightKg: e.target.value })} />
          </FormGroup>
        </div>
        {canSetOpeningStock && (
          <div className="mt-3">
            <div className="mb-1.5 flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wide text-amber-500"><Package size={12} className="mr-1 inline-block align-[-2px]" /> Opening Stock</span>
              <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-500">Permission Required</span>
            </div>
            <div className="w-56">
              <FormGroup
                label={editing ? 'Opening Qty (current)' : 'Opening Qty'}
                hint={editing ? 'Adjusts stock by the difference' : 'Sets initial warehouse stock'}
              >
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={form.openingQty}
                  onChange={(e) => setForm({ ...form, openingQty: e.target.value })}
                  placeholder="0"
                />
              </FormGroup>
            </div>
          </div>
        )}
      </FormSection>

      <FormSection title={<span className="inline-flex items-center gap-1.5"><FileText size={14} /> Additional Info</span>}>
        <FormGroup label="Description / Remarks">
          <Textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Item description, material details, remarks…"
          />
        </FormGroup>
        <div className="mt-3 rounded-md border border-danger/30 bg-danger/[0.06] px-3 py-2 text-[11px] text-danger">
          <AlertTriangle size={13} className="mr-1 inline-block align-[-2px]" /> <strong>Important:</strong> Barcode cannot be changed once saved. Cost-price changes do NOT auto-update MRP — use a separate MRP revision flow.
        </div>
      </FormSection>
    </Modal>
  );
}
